import type {
  CommonModel,
  IndeedConfig,
  IndeedRobinsearchCredentials,
  IndeedSubscriptionAdminResponse,
} from './indeed.types';
import { subscriptionInfoPayload } from './indeed.payloads';
import {
  extractCookieFromHeaders,
  extractCookieValue,
  extractCtkFromHeaders,
  getCapturedRequestHeaders,
} from './useIndeedConfig.interceptor';
import {
  buildIndeedHeaders,
  getIndeedHeadersFromCapturedHeaders,
  getIndeedHeadersFromCredentials,
} from './useIndeedConfig.headers';
import {
  convertSubscriptionGraphQLResponseToAdminResponse,
  getAdminFallbackSubscriptionState,
} from './useIndeedConfig.parser';
import { logIndeedConfigResolution, logIndeedSubscriptionInfoGraphQL } from './useIndeedConfig.logs';
import type { IndeedSubscriptionAdminResult } from './useIndeedConfig.types';
import { type Response as CurrentUser } from 'shared/legacy/hooks/useCurrentUserQuery';
import { browserExtensionClient } from 'clients/BrowserExtensionClient/BrowserExtensionClient';
import { logger } from 'config/logger';
import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';

export {
  getActiveSubscriptionFromAdminResponse,
  getAdminFallbackSubscriptionState,
  getHasExpiredSubscriptionFromAdminResponse,
  getSubscriptionAdminCreditBalance,
  getSubscriptionAdminEmail,
} from './useIndeedConfig.parser';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const buildIndeedConfigFromCredentials = (
  robinsearchCredentials: IndeedRobinsearchCredentials,
  adminSubscriptionState: ReturnType<typeof getAdminFallbackSubscriptionState>,
): IndeedConfig => ({
  isLoggedIn: true,
  status: 'Logged In',
  email: adminSubscriptionState?.email ?? robinsearchCredentials.username,
  csrfToken: robinsearchCredentials.csrf_token ?? '',
  csrfParam: '',
  creditBalance: adminSubscriptionState?.creditBalance ?? 0,
  hasExpiredSubscription: adminSubscriptionState?.hasExpiredSubscription ?? false,
  hasActiveSubscription: adminSubscriptionState?.hasActiveSubscription ?? true,
  headers: {
    ...buildIndeedHeaders({ ctk: robinsearchCredentials.ctk, apiKey: robinsearchCredentials.api_key }),
  },
});

const getIndeedConfigFromRobinsearch = async (
  subscriptionAdmin: IndeedSubscriptionAdminResponse,
  onStale?: () => void,
): Promise<IndeedConfig | null> => {
  const robinsearchCredentials = await campaignsClient.fetchIndeedCredentials();
  if (!robinsearchCredentials) return null;
  if (robinsearchCredentials?.status !== 'active') return null;

  if (Date.now() - robinsearchCredentials.captured_at > ONE_DAY_MS) {
    onStale?.();
  }

  const adminSubscriptionState = getAdminFallbackSubscriptionState(
    { emailAddress: robinsearchCredentials.username } as CommonModel,
    subscriptionAdmin,
  );

  return buildIndeedConfigFromCredentials(robinsearchCredentials, adminSubscriptionState);
};

const getIndeedConfigHasActiveSubscription = (config: IndeedConfig | null): boolean | undefined => {
  return config?.isLoggedIn ? config.hasActiveSubscription : undefined;
};

export const getConfigFromInterceptedHeaders = async (
  currentUser?: CurrentUser,
  onStale?: () => void,
): Promise<IndeedConfig | null> => {
  let subscriptionAdminResult: IndeedSubscriptionAdminResult = { data: null, source: 'none' };

  try {
    const capturedRequestHeaders = await getCapturedRequestHeaders();
    const subscriptionAdmin = await getSubscriptionAdminFromGraphQL(currentUser, capturedRequestHeaders?.headersMap);
    subscriptionAdminResult = { data: subscriptionAdmin, source: subscriptionAdmin ? 'graphql' : 'none' };

    if (!subscriptionAdmin) return null;

    if (capturedRequestHeaders) {
      const { headersMap } = capturedRequestHeaders;
      const cookieStr = extractCookieFromHeaders(headersMap);
      const ctk = extractCtkFromHeaders(headersMap, cookieStr);
      const apiKey = headersMap['indeed-api-key'];

      if (ctk && cookieStr && apiKey) {
        if (capturedRequestHeaders.capturedAt && Date.now() - capturedRequestHeaders.capturedAt > ONE_DAY_MS) {
          onStale?.();
        }

        let email: string | null = null;
        try {
          const ppid = extractCookieValue(cookieStr, 'PPID');
          if (ppid) email = JSON.parse(atob(ppid.split('.')[1])).email ?? null;
        } catch {
          /* no email */
        }

        const adminSubscriptionState = getAdminFallbackSubscriptionState(
          { emailAddress: email } as CommonModel,
          subscriptionAdmin,
        );

        const config = {
          isLoggedIn: true,
          status: 'Logged In',
          email: adminSubscriptionState?.email ?? email ?? '',
          csrfToken: extractCookieValue(cookieStr, 'CSRF'),
          csrfParam: extractCookieValue(cookieStr, 'ENC_CSRF'),
          creditBalance: adminSubscriptionState?.creditBalance ?? 0,
          hasExpiredSubscription: adminSubscriptionState?.hasExpiredSubscription ?? false,
          hasActiveSubscription: adminSubscriptionState?.hasActiveSubscription ?? true,
          headers: {
            ...buildIndeedHeaders({ ctk, apiKey, cookie: cookieStr }),
          },
        } satisfies IndeedConfig;

        campaignsClient.saveIndeedCredentials({
          ctk,
          username: config.email,
          status: 'active',
          platform: 'indeed',
          api_key: apiKey,
          csrf_token: config.csrfToken,
        });

        logIndeedConfigResolution({
          currentUser,
          outcome: 'success',
          source: subscriptionAdminResult.source,
          indeedEmail: config.email,
          creditBalance: config.creditBalance,
          hasActiveSubscription: config.hasActiveSubscription,
        });

        return config;
      } else {
        const config = await getIndeedConfigFromRobinsearch(subscriptionAdmin, onStale);

        logIndeedConfigResolution({
          currentUser,
          outcome: config ? 'success' : 'failed',
          source: subscriptionAdminResult.source,
          indeedEmail: config?.email,
          creditBalance: config?.creditBalance,
          hasActiveSubscription: getIndeedConfigHasActiveSubscription(config),
        });

        return config;
      }
    } else {
      const config = await getIndeedConfigFromRobinsearch(subscriptionAdmin, onStale);

      logIndeedConfigResolution({
        currentUser,
        outcome: config ? 'success' : 'failed',
        source: subscriptionAdminResult.source,
        indeedEmail: config?.email,
        creditBalance: config?.creditBalance,
        hasActiveSubscription: getIndeedConfigHasActiveSubscription(config),
      });

      return config;
    }
  } catch (error) {
    logger.error(error, { context: 'IndeedConfigFromInterceptedHeaders' });
    logIndeedConfigResolution({
      currentUser,
      outcome: 'failed',
      source: subscriptionAdminResult.source,
      error,
    });

    return null;
  }
};

export const getSubscriptionAdminFromGraphQL = async (
  currentUser?: CurrentUser,
  headersMap?: Record<string, string> | null,
): Promise<IndeedSubscriptionAdminResponse | null> => {
  try {
    const headers =
      getIndeedHeadersFromCapturedHeaders(headersMap ?? undefined) ??
      getIndeedHeadersFromCredentials(await campaignsClient.fetchIndeedCredentials());

    if (!headers) {
      logIndeedSubscriptionInfoGraphQL({ currentUser, outcome: 'missing_headers' });

      return null;
    }

    const response = await browserExtensionClient.sendMessage('proxyHttpRequest', {
      url: 'https://apis.indeed.com/graphql',
      method: 'POST',
      body: JSON.stringify(subscriptionInfoPayload),
      headers,
    });

    if (response.status !== 200) {
      logIndeedSubscriptionInfoGraphQL({
        currentUser,
        outcome: 'non_200_response',
        responseBody: response.body,
      });

      return null;
    }

    try {
      const parsedResponse = JSON.parse(response.body);

      if (parsedResponse.errors && parsedResponse.errors.length > 0) {
        logIndeedSubscriptionInfoGraphQL({
          currentUser,
          outcome: 'error',
          responseBody: response.body,
        });
        return null;
      }

      const result = convertSubscriptionGraphQLResponseToAdminResponse(parsedResponse);
      logIndeedSubscriptionInfoGraphQL({ currentUser, outcome: 'success', responseBody: response.body });
      return result;
    } catch (error) {
      logger.error(error, { context: 'IndeedSubscriptionInfoGraphQL' });
      logIndeedSubscriptionInfoGraphQL({ currentUser, outcome: 'error', responseBody: response.body, error });

      return null;
    }
  } catch (error) {
    logger.error(error, { context: 'IndeedSubscriptionInfoGraphQL' });
    logIndeedSubscriptionInfoGraphQL({ currentUser, outcome: 'error', error });

    return null;
  }
};
